/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { When } from "react-if";
import { useHistory } from "react-router-dom";
import {
  Row,
  Col,
  Tabs,
  Tab,
  Heading,
  SVGIcon,
  Spacer,
  theme,
  Message,
  Button,
  Text,
  Label,
  Description,
} from "@nordcloud/gnui";
import {
  ExternalIntegrationsDocument,
  useAddExternalIntegrationMutation,
} from "~/generated/graphql";
import {
  BreadcrumbsBox,
  Fieldset,
  InfoCard,
  InputField,
  LoaderWrap,
} from "~/components";
import { ContactsInput } from "~/components/Inputs";
import { SUCCESS_TEXT, ERROR_TEXT } from "~/constants";
import { useFeature } from "~/hooks/useFeature";
import { ROUTES } from "~/routing/routes";
import { useCustomers } from "~/services/customers";
import { showSuccess, showError } from "~/services/toast";
import { StepContainer } from "~/views/accounts/AddAccount/components/StepContainer";
import { SchemaExternalIntegration } from "~/views/externalIntegrations/AddIntegration/validators";
import { AddSandbox } from "~/views/externalIntegrations/components/AddSandbox";
import { ReadOnlyField } from "./components/ReadOnlyField";

enum FIELDS {
  name = "name",
  ownerId = "ownerId",
  description = "description",
}

const breadcrumbLabels = [
  {
    value: "external-integrations",
    label: "External Integrations",
  },
  {
    value: "add-integration",
    label: "Add Integration",
  },
];

type FormData = {
  [FIELDS.name]: string;
  [FIELDS.ownerId]: string;
  [FIELDS.description]: string;
};

export function AddIntegration() {
  const history = useHistory();
  const [currentStep, setCurrentStep] = React.useState(0);
  const [ownerId, setOwnerId] = React.useState("");

  const useExperimental = useFeature("allowExperimental");
  const { selectedCustomer } = useCustomers();
  const isSandbox = selectedCustomer?.name.toLowerCase().includes("sandbox");

  const [addExternalIntegration, { data: externalIntegrationData, loading }] =
    useAddExternalIntegrationMutation({
      refetchQueries: [{ query: ExternalIntegrationsDocument }],
    });

  const {
    register,
    handleSubmit,
    setValue,

    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(SchemaExternalIntegration),
  });

  const handleSteps = (key: number) => {
    setCurrentStep(key);
  };
  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const handleAddIntegration = async (data: FormData) => {
    const integration = { ...data, ownerId };
    try {
      await addExternalIntegration({
        variables: {
          integration,
        },
      });
      nextStep();
    } catch {
      showError(ERROR_TEXT.default);
    }
  };

  return (
    <Row>
      <Col>
        <BreadcrumbsBox
          title="Add External Integration"
          labels={breadcrumbLabels}
        />
        <Tabs wizard step={currentStep} handleTab={handleSteps}>
          <Tab
            disabled
            heading="General Information"
            caption=""
            buttonsJustify="center"
            buttons={
              <LoaderWrap loading={loading}>
                <Button form="addIntegrationForm" type="submit">
                  Next Step
                </Button>
              </LoaderWrap>
            }
          >
            <form
              id="addIntegrationForm"
              onSubmit={handleSubmit(handleAddIntegration)}
            >
              <Row>
                <Col sm={8} md={4} lg={6} offset={{ sm: 0, md: 2 }}>
                  <InputField
                    disabled={loading}
                    label="Integration Name"
                    {...register(FIELDS.name, { required: true })}
                    required
                    error={errors.name}
                    placeholder="Integration Name"
                  />
                  <InputField
                    disabled={loading}
                    label="Integration Description"
                    {...register(FIELDS.description)}
                    error={errors.description}
                    placeholder="Integration Description"
                  />
                  <Fieldset>
                    <Label required name="Integration Owner" />
                    <ContactsInput
                      disabled={loading}
                      name={FIELDS.ownerId}
                      placeholder="Select integration owner"
                      value={[ownerId]}
                      status={errors.ownerId && "error"}
                      onChange={(id) => {
                        try {
                          setOwnerId(id);
                          setValue(FIELDS.ownerId, id);
                          showSuccess(SUCCESS_TEXT.integrationOwnerAdded);
                        } catch {
                          showError(ERROR_TEXT.default);
                        }
                      }}
                    />
                    <When condition={errors.ownerId != null}>
                      <Description>Integration owner is required.</Description>
                    </When>
                  </Fieldset>
                </Col>
              </Row>
            </form>
          </Tab>
          <Tab
            disabled
            heading="Summary"
            caption=""
            buttons={<Spacer height={theme.spacing.spacing08} />}
          >
            <Row justify="center">
              <Col sm={6} md={6} lg={8}>
                <Spacer height="1rem" />
                <Row>
                  <Col>
                    <Message status="success" image="success">
                      External integration has been added
                    </Message>
                  </Col>
                </Row>
                <Spacer height="2rem" />
                <StepContainer counter={1}>
                  <Text>Copy and save ID and Secret</Text>
                  <Description>
                    Please use the ID and Secret below to bind our external
                    integration. This ID and Secret can only be generated once,
                    please keep it properly.
                  </Description>
                  <ReadOnlyField
                    label="ID"
                    value={
                      externalIntegrationData?.addExternalIntegration?.id ?? ""
                    }
                  />
                  <ReadOnlyField
                    label="Secret"
                    value={
                      externalIntegrationData?.addExternalIntegration?.secret ||
                      ""
                    }
                  />
                </StepContainer>
                <StepContainer counter={2}>
                  <Text>Link to the Documentation</Text>
                </StepContainer>
                <When condition={useExperimental && !isSandbox}>
                  <StepContainer counter={3}>
                    <Text>Create Sandbox</Text>
                    <AddSandbox />
                  </StepContainer>
                </When>
                <Row>
                  <Col lg={4}>
                    <InfoCard
                      align="center"
                      onClick={() => {
                        history.push(
                          `${ROUTES.externalIntegrations.index}/${externalIntegrationData?.addExternalIntegration?.nid}`
                        );
                      }}
                    >
                      <SVGIcon name="detailsView" size="xl" />
                      <Heading marginTop="1rem" level={4}>
                        Integration Details
                      </Heading>
                    </InfoCard>
                  </Col>
                  <Col lg={4}>
                    <InfoCard
                      align="center"
                      onClick={() =>
                        history.push(ROUTES.externalIntegrations.index)
                      }
                    >
                      <SVGIcon name="listView" size="xl" />
                      <Heading marginTop="1rem" level={4}>
                        List of Integrations
                      </Heading>
                    </InfoCard>
                  </Col>
                  <Col lg={4}>
                    <InfoCard
                      align="center"
                      onClick={() => {
                        setCurrentStep(0);
                        setOwnerId("");
                        setValue(FIELDS.ownerId, "");
                      }}
                    >
                      <SVGIcon name="wizardView" size="xl" />
                      <Heading marginTop="1rem" level={4}>
                        Add Another Integration
                      </Heading>
                    </InfoCard>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Tab>
        </Tabs>
      </Col>
    </Row>
  );
}
